/**
 * usePhotostudioElements Hook
 *
 * Provides CRUD operations for the Photostudio elements library.
 */

import { useCallback, useState, useMemo } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import type {
  IPhotostudioElement,
  IUsePhotostudioElementsReturn,
  ElementType,
} from '@/@types/photostudio';
import { validateImageFile } from '@/@types/photostudio';

export function usePhotostudioElements(): IUsePhotostudioElementsReturn {
  const [error, setError] = useState<string | null>(null);

  // Queries
  const elementsData = useQuery(api.photostudioElements.getActiveElements);
  const elements: IPhotostudioElement[] = useMemo(
    () => (elementsData ?? []) as IPhotostudioElement[],
    [elementsData]
  );

  // Mutations
  const createElementMutation = useMutation(api.photostudioElements.createElement);
  const updateElementMutation = useMutation(api.photostudioElements.updateElement);
  const deleteElementMutation = useMutation(api.photostudioElements.deleteElement);
  const generateUploadUrlMutation = useMutation(api.photostudioElements.generateUploadUrl);

  // Loading state
  const isLoading = elementsData === undefined;

  // Create element
  const createElement = useCallback(
    async (
      type: ElementType,
      name: string,
      imageId: Id<'_storage'>,
      description?: string
    ): Promise<{ success: boolean; elementId?: Id<'photostudioElements'>; error?: string }> => {
      setError(null);

      try {
        const result = await createElementMutation({
          type,
          name,
          imageId,
          description,
        });

        if (!result.success) {
          setError(result.error || 'Failed to create element');
        }

        return result;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to create element';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [createElementMutation]
  );

  // Update element
  const updateElement = useCallback(
    async (
      elementId: Id<'photostudioElements'>,
      name: string,
      description?: string
    ): Promise<{ success: boolean; error?: string }> => {
      setError(null);

      try {
        const result = await updateElementMutation({
          elementId,
          name,
          description,
        });

        if (!result.success) {
          setError(result.error || 'Failed to update element');
        }

        return result;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update element';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [updateElementMutation]
  );

  // Delete element
  const deleteElement = useCallback(
    async (elementId: Id<'photostudioElements'>): Promise<{ success: boolean; error?: string }> => {
      setError(null);

      try {
        const result = await deleteElementMutation({ elementId });

        if (!result.success) {
          setError(result.error || 'Failed to delete element');
        }

        return result;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete element';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [deleteElementMutation]
  );

  // Upload image and create element
  const uploadAndCreateElement = useCallback(
    async (
      type: ElementType,
      name: string,
      file: File,
      description?: string
    ): Promise<{ success: boolean; elementId?: Id<'photostudioElements'>; error?: string }> => {
      setError(null);

      try {
        // Validate file
        const validation = validateImageFile(file);
        if (!validation.valid) {
          setError(validation.error || 'Invalid file');
          return { success: false, error: validation.error };
        }

        // Get upload URL
        const uploadUrl = await generateUploadUrlMutation();

        // Upload file
        const response = await fetch(uploadUrl, {
          method: 'POST',
          headers: { 'Content-Type': file.type },
          body: file,
        });

        if (!response.ok) {
          throw new Error('Failed to upload image');
        }

        const { storageId } = (await response.json()) as { storageId: Id<'_storage'> };

        // Create element
        return await createElement(type, name, storageId, description);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to upload and create element';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [generateUploadUrlMutation, createElement]
  );

  // Get elements by type
  const getElementsByType = useCallback(
    (type: ElementType): IPhotostudioElement[] => {
      return elements.filter(e => e.type === type);
    },
    [elements]
  );

  // Memoized filtered elements
  const outfits = useMemo(() => getElementsByType('outfit'), [getElementsByType]);
  const props = useMemo(() => getElementsByType('prop'), [getElementsByType]);
  const scenes = useMemo(() => getElementsByType('scene'), [getElementsByType]);

  return {
    elements,
    outfits,
    props,
    scenes,
    isLoading,
    error,
    createElement,
    updateElement,
    deleteElement,
    uploadAndCreateElement,
    getElementsByType,
  };
}
